import type { ZoneDefinition } from '@/types';

export type ZoneFieldError = 'min' | 'max' | 'continuity';

export interface ZoneErrorDetail {
  /** Which input on the zone row triggered the error. */
  field: ZoneFieldError;
  /** Translation-key (resolved by the UI) used to render the user-facing message. */
  messageKey: 'errZoneRange' | 'errZoneContinuity';
  /** Index in the zones array — handy for input focus / scroll. */
  index: number;
  /** Pre-rendered human message in case the caller doesn't go through i18n. */
  message: string;
}

export interface ZoneValidationResult {
  isValid: boolean;
  /** Per-zone-id error map. The zone with continuity break is the LOWER neighbour (zone N). */
  errors: Record<string, ZoneErrorDetail>;
}

/** Small absolute tolerance to absorb floating-point arithmetic (zones are stored as 0..2 fractions). */
const CONTIGUITY_EPSILON = 1e-6;

/**
 * Enforce two invariants on the zones table:
 *  1. RANGE — every zone has `max > min` (strictly, after epsilon).
 *  2. CONTINUITY — for every consecutive pair (N, N+1) the upper bound of N must equal
 *     the lower bound of N+1, so the zones cover the intensity axis without gaps or overlaps.
 *
 * The result is shaped for direct consumption by `ZonesDisplay`:
 *   - `errors[zone.id]` describes which input to redden + which i18n key to render.
 *   - `isValid` short-circuits to false on the first failure so callers can disable buttons.
 *
 * The function is PURE and stateless — safe to call inside `useMemo` on every render.
 */
export function validateZones(zones: readonly ZoneDefinition[]): ZoneValidationResult {
  const errors: Record<string, ZoneErrorDetail> = {};
  if (!Array.isArray(zones) || zones.length === 0) {
    return { isValid: false, errors };
  }

  for (let i = 0; i < zones.length; i++) {
    const z = zones[i];
    if (!Number.isFinite(z.min) || !Number.isFinite(z.max) || z.max <= z.min + CONTIGUITY_EPSILON) {
      errors[z.id] = {
        field: 'max',
        messageKey: 'errZoneRange',
        index: i,
        message: `${z.id}: górny limit musi być większy niż dolny.`,
      };
    }
  }

  for (let i = 0; i < zones.length - 1; i++) {
    const current = zones[i];
    const next = zones[i + 1];
    if (Math.abs(current.max - next.min) > CONTIGUITY_EPSILON) {
      // Mark the LOWER neighbour's `max` as the broken input — that's the field the
      // user would naturally fix to restore continuity (or, equivalently, edit next.min).
      errors[current.id] = errors[current.id] ?? {
        field: 'continuity',
        messageKey: 'errZoneContinuity',
        index: i,
        message: `${current.id} kończy się ${formatPct(current.max)}, a ${next.id} zaczyna od ${formatPct(next.min)}.`,
      };
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function formatPct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

/** Deep-equal comparison used by the UI to flip the "Własne (Custom)" badge on the first edit. */
export function zonesEqual(a: readonly ZoneDefinition[], b: readonly ZoneDefinition[]): boolean {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const za = a[i];
    const zb = b[i];
    if (za.id !== zb.id) return false;
    if (Math.abs(za.min - zb.min) > CONTIGUITY_EPSILON) return false;
    if (Math.abs(za.max - zb.max) > CONTIGUITY_EPSILON) return false;
  }
  return true;
}
